// Diagnostics are a finite allowlist, not redaction of arbitrary application data.
const MESSAGES = Object.freeze({
  Error: 'An error occurred.', NetworkError: 'A network request failed.',
  ReactError: 'A rendering error occurred.', TypeError: 'A type error occurred.',
  ReferenceError: 'A reference error occurred.', SyntaxError: 'A syntax error occurred.',
  RangeError: 'A range error occurred.', URIError: 'A URI error occurred.',
  AbortError: 'An operation was aborted.', TimeoutError: 'An operation timed out.',
});
const CATEGORIES = new Set([
  'admin', 'partner', 'auth', 'login', 'register', 'search', 'listings',
  'properties', 'rooms', 'enquiries', 'offers', 'quotes', 'bookings', 'stays',
  'extensions', 'support', 'refunds', 'reviews', 'rewards', 'finance',
  'commissions', 'booking-confirmation', 'booking-verification', 'crash-reports',
  'dashboard', 'config', 'documents', 'uploads',
]);
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const member = (value, values, fallback = null) => typeof value === 'string' && values.includes(value) ? value : fallback;

export const diagnosticMethod = value => member(value, METHODS, 'GET');
export const diagnosticStatus = value => Number.isInteger(value) && value >= 100 && value <= 599 ? value : null;

export function diagnosticRoute(value) {
  if (typeof value !== 'string' || value.length > 4096) return null;
  const path = value.replace(/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS) /, '').split(/[?#]/, 1)[0];
  if (path === '/') return '/';
  // No URL decoding or arbitrary path segments. Only fixed relative categories.
  const match = path.match(/^\/(?:api\/(?:v[12]\/)?|v[12]\/)?([^/]+)(?:\/|$)/);
  return match && CATEGORIES.has(match[1]) ? `/${match[1]}` : null;
}

export function sanitizeCrashReport(input = {}) {
  const data = input && typeof input === 'object' ? input : {};
  const errorType = member(data.error_type, Object.keys(MESSAGES), 'Error');
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera'
    : /Chrome/.test(ua) ? 'Chrome' : /Firefox/.test(ua) ? 'Firefox' : /Safari/.test(ua) ? 'Safari' : 'Unknown';
  const os = /Android/.test(ua) ? 'Android' : /iPhone|iPad|iOS/.test(ua) ? 'iOS'
    : /Win/.test(ua) ? 'Windows' : /Mac/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Unknown';
  const width = typeof window === 'undefined' ? 1024 : window.innerWidth;
  return {
    timestamp: new Date().toISOString(), side: 'frontend', role: 'ANONYMOUS',
    environment: member(process.env.REACT_APP_ENVIRONMENT, ['development', 'staging', 'production'],
      process.env.NODE_ENV === 'production' ? 'production' : 'development'),
    route_or_screen: diagnosticRoute(data.route_or_screen), endpoint: diagnosticRoute(data.endpoint),
    http_status: diagnosticStatus(data.http_status), error_type: errorType,
    message: MESSAGES[errorType], stack_trace: '[REDACTED]',
    severity: member(data.severity, ['INFO', 'WARNING', 'ERROR', 'CRITICAL'], 'ERROR'),
    browser, os, device_type: width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop',
    metadata: {
      privacy_version: 1, method: diagnosticMethod(data.metadata?.method),
      source: member(data.metadata?.source, ['network', 'rtk_query', 'GlobalErrorBoundary', 'manual'], 'manual'),
    },
  };
}